export type DesktopNotificationMode = "always" | "when-unfocused" | "never";

export type TaskNotificationSettings = {
  inAppEnabled: boolean;
  desktopNotificationMode: DesktopNotificationMode;
};

export type SummaryTaskNotificationStage = "outline" | "summary";

export type PendingSummaryTask = {
  summaryId: string;
  stage: SummaryTaskNotificationStage;
  workflowId?: string;
};

export type SummaryTaskNotificationOutcome = "succeeded" | "failed" | "stale" | null;

export type StorageLike = Pick<Storage, "getItem" | "setItem">;

export const defaultTaskNotificationSettings: TaskNotificationSettings = {
  inAppEnabled: true,
  desktopNotificationMode: "when-unfocused",
};

export function readTaskNotificationSettings(storage: StorageLike | null): TaskNotificationSettings {
  if (!storage) return defaultTaskNotificationSettings;

  try {
    const stored = JSON.parse(storage.getItem("findin:task-notification-settings") || "{}");
    if (!stored || typeof stored !== "object") return defaultTaskNotificationSettings;

    return {
      inAppEnabled: typeof stored.inAppEnabled === "boolean"
        ? stored.inAppEnabled
        : defaultTaskNotificationSettings.inAppEnabled,
      desktopNotificationMode: isDesktopNotificationMode(stored.desktopNotificationMode)
        ? stored.desktopNotificationMode
        : defaultTaskNotificationSettings.desktopNotificationMode,
    };
  } catch {
    return defaultTaskNotificationSettings;
  }
}

export function writeTaskNotificationSettings(storage: StorageLike | null, settings: TaskNotificationSettings) {
  storage?.setItem("findin:task-notification-settings", JSON.stringify(settings));
}

export function readTrackedResearchDrawingTaskIds(storage: StorageLike | null) {
  if (!storage) return [];

  try {
    const stored = JSON.parse(storage.getItem("findin:pending-research-drawing-tasks") || "[]");
    return Array.isArray(stored)
      ? stored.filter((taskId): taskId is string => typeof taskId === "string" && Boolean(taskId.trim()))
      : [];
  } catch {
    return [];
  }
}

export function trackResearchDrawingTaskInStorage(storage: StorageLike | null, taskId: number | string) {
  const normalizedTaskId = String(taskId).trim();
  if (!storage || !normalizedTaskId) return;

  const taskIds = readTrackedResearchDrawingTaskIds(storage);
  if (!taskIds.includes(normalizedTaskId)) {
    storage.setItem("findin:pending-research-drawing-tasks", JSON.stringify([...taskIds, normalizedTaskId]));
  }
}

export function untrackResearchDrawingTaskInStorage(storage: StorageLike | null, taskId: number | string) {
  if (!storage) return;

  const normalizedTaskId = String(taskId).trim();
  storage.setItem(
    "findin:pending-research-drawing-tasks",
    JSON.stringify(readTrackedResearchDrawingTaskIds(storage).filter((id) => id !== normalizedTaskId)),
  );
}

export function readTrackedSummaryTasks(storage: StorageLike | null): PendingSummaryTask[] {
  if (!storage) return [];

  try {
    const stored = JSON.parse(storage.getItem("findin:pending-summary-tasks") || "[]");
    if (!Array.isArray(stored)) return [];
    return stored.filter((task): task is PendingSummaryTask => (
      Boolean(task)
      && typeof task === "object"
      && typeof task.summaryId === "string"
      && Boolean(task.summaryId.trim())
      && (task.stage === "outline" || task.stage === "summary")
      && (task.workflowId === undefined || (typeof task.workflowId === "string" && Boolean(task.workflowId.trim())))
    ));
  } catch {
    return [];
  }
}

export function trackSummaryTaskInStorage(
  storage: StorageLike | null,
  summaryId: number | string,
  stage: SummaryTaskNotificationStage,
  workflowId?: number | string,
) {
  const normalizedSummaryId = String(summaryId).trim();
  if (!storage || !normalizedSummaryId) return;

  const normalizedWorkflowId = workflowId === undefined ? undefined : String(workflowId).trim();
  const tasks = readTrackedSummaryTasks(storage).filter((task) => task.summaryId !== normalizedSummaryId);
  storage.setItem("findin:pending-summary-tasks", JSON.stringify([
    ...tasks,
    {
      summaryId: normalizedSummaryId,
      stage,
      ...(normalizedWorkflowId ? { workflowId: normalizedWorkflowId } : {}),
    },
  ]));
}

export function untrackSummaryTaskInStorage(
  storage: StorageLike | null,
  summaryId: number | string,
  stage?: SummaryTaskNotificationStage,
) {
  if (!storage) return;

  const normalizedSummaryId = String(summaryId).trim();
  storage.setItem(
    "findin:pending-summary-tasks",
    JSON.stringify(readTrackedSummaryTasks(storage).filter((task) => (
      task.summaryId !== normalizedSummaryId || (stage !== undefined && task.stage !== stage)
    ))),
  );
}

export function shouldDeliverDesktopNotification(
  mode: DesktopNotificationMode,
  state: {
    desktopFocused?: boolean;
    pageVisible?: boolean;
    pageFocused?: boolean;
  },
) {
  if (mode === "never") return false;
  if (mode === "always") return true;
  if (state.desktopFocused !== undefined) return !state.desktopFocused;

  return state.pageVisible !== true || state.pageFocused !== true;
}

export function shouldDeliverInAppNotification(settings: TaskNotificationSettings) {
  return settings.inAppEnabled;
}

export function resolveSummaryTaskNotificationOutcome(
  trackedStage: SummaryTaskNotificationStage,
  currentStep: unknown,
  status: unknown,
): SummaryTaskNotificationOutcome {
  if (currentStep !== trackedStage) {
    // 大纲完成后接口会推进到 summary 阶段；此时应补发大纲完成提醒，
    // 不能像普通过期记录一样静默移除。
    return trackedStage === "outline" && currentStep === "summary" ? "succeeded" : "stale";
  }
  if (status === 2) return "succeeded";
  if (typeof status === "number" && status < 0) return "failed";
  return null;
}

export function createTaskNotificationDeduper(maxKeys = 100) {
  const keys = new Set<string>();
  const keyOrder: string[] = [];

  return {
    add(key: string) {
      if (keys.has(key)) return false;

      keys.add(key);
      keyOrder.push(key);
      if (keyOrder.length > maxKeys) {
        const expiredKey = keyOrder.shift();
        if (expiredKey) keys.delete(expiredKey);
      }
      return true;
    },
  };
}

function isDesktopNotificationMode(value: unknown): value is DesktopNotificationMode {
  return value === "always" || value === "when-unfocused" || value === "never";
}
